import { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_URL } from '../utils/api';
import { AuthContext } from '../context/AuthContext';
import axios from 'axios';
import { ArrowLeft, Save, Download, CloudRain, Sun, RefreshCw, AlertCircle, Trash2, AlertTriangle, Camera, Image as ImageIcon, Eye, EyeOff, ChevronDown, ChevronUp, Maximize2, Minimize2, MapPin } from 'lucide-react';
import ImageCropper from '../components/common/ImageCropper';
import LocationMap from '../components/common/LocationMap';
import { nativeDownload } from '../utils/nativeDownload';

export default function ApprenticeWeekDetail() {
    const { fetchWithAuth } = useContext(AuthContext);
    const { id } = useParams();
    const navigate = useNavigate();
    const [confirmModal, setConfirmModal] = useState({ show: false, title: '', message: '', onConfirm: null });
    const [week, setWeek] = useState(null);
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [selectedImage, setSelectedImage] = useState(null);
    const [expandedDays, setExpandedDays] = useState(new Set()); // All collapsed by default
    const [croppingIndex, setCroppingIndex] = useState(null);
    const [suggestingIndex, setSuggestingIndex] = useState(null);

    const WEEKDAYS = [
        { key: 'MONDAY', label: 'Montag' },
        { key: 'TUESDAY', label: 'Dienstag' },
        { key: 'WEDNESDAY', label: 'Mittwoch' },
        { key: 'THURSDAY', label: 'Donnerstag' },
        { key: 'FRIDAY', label: 'Freitag' },
        { key: 'SATURDAY', label: 'Samstag' },
        { key: 'SUNDAY', label: 'Sonntag' },
    ];

    useEffect(() => {
        if (id === 'new') {
            createNewWeek();
        } else {
            fetchWeekDetail();
        }
    }, [id]);

    const createNewWeek = async () => {
        try {
            // Read optional ?year=YYYY&week=WW param 
            const searchParams = new URLSearchParams(window.location.search);
            const passedYear = searchParams.get('year');
            const passedWeek = searchParams.get('week');

            let year, weekNo;

            if (passedYear && passedWeek) {
                year = parseInt(passedYear);
                weekNo = parseInt(passedWeek);
            } else {
                // Fallback: Calculate current week
                const now = new Date();
                const d = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
                const dayNum = d.getUTCDay() || 7;
                d.setUTCDate(d.getUTCDate() + 4 - dayNum);
                year = d.getUTCFullYear();
                const yearStart = new Date(Date.UTC(year, 0, 1));
                weekNo = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
            }

            const payload = {
                year,
                week_number: weekNo,
                location_city: '' // Let user fill it out before weather fetching
            };

            const response = await fetchWithAuth('/api/apprentice/weeks/', {
                method: 'POST',
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                const data = await response.json();
                navigate(`/apprentice/week/${data.id}`, { replace: true });
            } else {
                let err = {};
                try {
                    err = await response.json();
                } catch(e) {
                    err = { detail: "Ungültige Serverantwort. Womöglich existiert die Woche bereits." };
                }
                
                // Fallback: If creation fails because it exists (400 Bad Request usually duplicate), fetch it and redirect
                if (response.status === 400 || response.status === 500 || (err.non_field_errors && err.non_field_errors[0])) {
                    try {
                        const getRes = await fetchWithAuth(`/api/apprentice/weeks/?year=${payload.year}`);
                        if (getRes.ok) {
                            const data = await getRes.json();
                            const weeks = Array.isArray(data) ? data : (data.results || []);
                            const existingWeek = weeks.find(w => w.week_number === parseInt(payload.week_number));
                            if (existingWeek) {
                                navigate(`/apprentice/week/${existingWeek.id}`, { replace: true });
                                return;
                            }
                        }
                    } catch (e) {
                        console.error('Failed to fetch existing week fallback', e);
                    }
                }
                
                setError(err.non_field_errors || err.detail || 'Fehler beim Erstellen der Woche. Möglicherweise existiert diese KW bereits.');
                setLoading(false);
            }
        } catch (e) {
            setError(e.message);
            setLoading(false);
        }
    };

    const fetchWeekDetail = async () => {
        try {
            const response = await fetchWithAuth(`/api/apprentice/weeks/${id}/`);
            if (response.ok) {
                const data = await response.json();
                setWeek(data);

                // Normalise reports array for form handling
                const normalized = WEEKDAYS.map(wd => {
                    const existing = data.daily_reports?.find(r => r.weekday === wd.key);
                    return existing || {
                        id: null,
                        weekday: wd.key,
                        date: calculateDateForWeekday(data.start_date, wd.key),
                        location: '',
                        activities: '',
                        school_topics: '',
                        training_center: '',
                        work_hours: 8.0,
                        temp_max: null,
                        temp_min: null,
                        precipitation: null,
                        weather: '',
                        report_type: existing?.report_type || 'BETRIEB',
                        image: null,
                        show_image: true,
                        latitude: existing?.latitude || null,
                        longitude: existing?.longitude || null,
                        show_map: existing?.show_map ?? true
                    };
                });
                setReports(normalized);
            } else {
                setError('Woche konnte nicht geladen werden.');
            }
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    const calculateDateForWeekday = (startDateStr, weekdayKey) => {
        const d = new Date(startDateStr);
        const idx = WEEKDAYS.findIndex(w => w.key === weekdayKey);
        d.setDate(d.getDate() + idx);
        return d.toISOString().split('T')[0];
    };

    const handleReportChange = (index, field, value) => {
        const updated = [...reports];
        updated[index] = { ...updated[index], [field]: value };
        setReports(updated);
    };

    const saveReport = async (report) => {
        if (!report.activities && !report.school_topics && !report.training_center &&
            report.temp_min === null && report.temp_max === null &&
            report.precipitation === null && !report.weather && !report.location) {
            return;
        }

        const payload = {
            week: id,
            date: report.date,
            weekday: report.weekday,
            location: report.location,
            activities: report.activities || '',
            school_topics: report.school_topics || '',
            training_center: report.training_center || '',
            work_hours: report.work_hours || 8.0,
            temp_min: report.temp_min,
            temp_max: report.temp_max,
            precipitation: report.precipitation,
            weather: report.weather || '',
            report_type: report.report_type || 'BETRIEB',
            show_image: report.show_image ?? true,
            latitude: report.latitude,
            longitude: report.longitude,
            show_map: report.show_map ?? true
        };

        const formData = new FormData();
        Object.keys(payload).forEach(key => {
            if (payload[key] !== null && payload[key] !== undefined) {
                formData.append(key, payload[key]);
            }
        });

        // Add image as a blob if it's new (a Blob/File object)
        if (report.image instanceof Blob || report.image instanceof File) {
            formData.append('image', report.image, `daily_report_${report.weekday}.jpg`);
        }

        const endpoint = report.id
            ? `${API_URL}/api/apprentice/reports/${report.id}/`
            : `${API_URL}/api/apprentice/reports/`;

        const method = report.id ? 'PUT' : 'POST';

        const response = await fetchWithAuth(endpoint, {
            method,
            body: formData,
            // fetchWithAuth will handle multipart automatically if it's FormData
            noJson: true 
        });

        if (!response.ok) {
            const err = await response.json();
            throw new Error(err.detail || JSON.stringify(err));
        }
        return await response.json();
    };

    const handleAutoSave = async (index) => {
        try {
            const report = reports[index];
            const savedData = await saveReport(report);
            if (savedData && !report.id) {
                // Update local state with the new ID from server
                const updated = [...reports];
                updated[index] = { ...updated[index], id: savedData.id };
                setReports(updated);
            }
        } catch (e) {
            console.error('Autosave error:', e);
            setError('Autosave fehlgeschlagen: ' + e.message);
        }
    };

    const handleAutoSaveWeek = async (updatedWeek = null) => {
        try {
            const currentWeek = updatedWeek || week;
            await fetchWithAuth(`/api/apprentice/weeks/${id}/`, {
                method: 'PATCH',
                body: JSON.stringify({ 
                    location_city: currentWeek.location_city,
                    font_style: currentWeek.font_style 
                })
            });
        } catch (e) {
            console.error('Autosave week error:', e);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        setError('');
        setSuccess('');
        try {
            // 1. Save general week Info
            await handleAutoSaveWeek();

            // 2. Save all reports
            for (let i = 0; i < reports.length; i++) {
                await saveReport(reports[i]);
            }

            setSuccess('Berichte erfolgreich gespeichert!');
            fetchWeekDetail(); // Reload to get IDs and sync
        } catch (e) {
            setError('Fehler beim Speichern: ' + e.message);
        } finally {
            setSaving(false);
            setTimeout(() => setSuccess(''), 3000);
        }
    };

    const handleRefreshWeather = async () => {
        const city = week?.location_city?.trim();
        if (!city) {
            setError('Bitte gib erst einen Einsatzort an (Stadt oder Ort) und speichere.');
            return;
        }

        setSaving(true);
        setError('');
        try {
            // Auto-save the city first so the backend refresh action has it
            await handleAutoSaveWeek();

            const res = await fetchWithAuth(`/api/apprentice/weeks/${id}/refresh_weather/`, {
                method: 'POST'
            });
            if (res.ok) {
                setSuccess('Wetterdaten erfolgreich aktualisiert!');
                fetchWeekDetail();
            } else {
                const err = await res.json();
                setError(err.error || 'Fehler beim Wetterabruf');
            }
        } catch (e) {
            setError(e.message);
        } finally {
            setSaving(false);
        }
    };

    const handleDownloadPdf = async () => {
        const token = localStorage.getItem('authToken');
        await nativeDownload(`/api/apprentice/weeks/${id}/download_pdf/`, token, (msg) => setError(msg));
    };

    const handleDelete = () => {
        setConfirmModal({
            show: true,
            title: 'Woche unwiderruflich löschen',
            message: 'Möchtest du diese Berichtswoche wirklich unwiderruflich löschen? Alle täglichen Berichte dieser Woche gehen verloren.',
            onConfirm: async () => {
                setSaving(true);
                try {
                    const response = await fetchWithAuth(`/api/apprentice/weeks/${id}/`, {
                        method: 'DELETE'
                    });
                    if (response.ok || response.status === 204) {
                        navigate('/apprentice-journal', { replace: true });
                    } else {
                        setError('Fehler beim Löschen der Woche.');
                        setSaving(false);
                    }
                } catch (e) {
                    setError('Fehler beim Löschen: ' + e.message);
                    setSaving(false);
                }
            }
        });
    };

    const handleImageSelect = (index, e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.addEventListener('load', () => {
                setSelectedImage(reader.result);
                setCroppingIndex(index);
            });
            reader.readAsDataURL(file);
        }
    };

    const handleCropComplete = async (croppedBlob) => {
        const updated = [...reports];
        updated[croppingIndex] = { ...updated[croppingIndex], image: croppedBlob };
        setReports(updated);
        setCroppingIndex(null);
        setSelectedImage(null);
        
        // Autosave the image
        try {
            await saveReport(updated[croppingIndex]);
            setSuccess('Bild erfolgreich gespeichert!');
            setTimeout(() => setSuccess(''), 2000);
        } catch (e) {
            setError('Fehler beim Speichern des Bildes: ' + e.message);
        }
    };

    const removeImage = async (index) => {
        const updated = [...reports];
        const report = updated[index];
        
        if (report.id) {
            try {
                // Clear image on server
                const formData = new FormData();
                formData.append('image', ''); 
                await fetchWithAuth(`${API_URL}/api/apprentice/reports/${report.id}/`, {
                    method: 'PATCH',
                    body: formData,
                    noJson: true
                });
            } catch (e) {
                console.error(e);
            }
        }
        
        updated[index] = { ...updated[index], image: null };
        setReports(updated);
    };

    const handleAiSuggest = async (index) => {
        const report = reports[index];
        if (!report.id || !report.image) {
            setError('Bitte erst das Bild speichern, um eine KI-Vorschau zu generieren.');
            return;
        }

        setSuggestingIndex(index);
        try {
            const res = await fetchWithAuth(`/api/apprentice/reports/${report.id}/ai_suggest_activities/`, {
                method: 'POST'
            });
            if (res.ok) {
                const data = await res.json();
                if (data.suggestion) {
                    const updated = [...reports];
                    // Append or replace? Let's replace if it's empty, otherwise append
                    if (!updated[index].activities || updated[index].activities.trim() === '') {
                        updated[index].activities = data.suggestion;
                    } else {
                        updated[index].activities += '\n\n' + data.suggestion;
                    }
                    setReports(updated);
                    setSuccess('KI-Vorschlag generiert!');
                    setTimeout(() => setSuccess(''), 2000);
                }
            } else {
                const err = await res.json();
                setError(err.error || 'Fehler bei der KI-Analyse');
            }
        } catch (e) {
            setError('KI-Fehler: ' + e.message);
        } finally {
            setSuggestingIndex(null);
        }
    };

    const toggleDay = (idx) => {
        const next = new Set(expandedDays);
        if (next.has(idx)) next.delete(idx);
        else next.add(idx);
        setExpandedDays(next);
    };

    const expandAll = () => setExpandedDays(new Set([0, 1, 2, 3, 4, 5, 6]));
    const collapseAll = () => setExpandedDays(new Set());

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-plantlix-500 border-t-transparent"></div>
            </div>
        );
    }

    if (error && !week) {
        return (
            <div className="min-h-screen p-8 text-center text-red-600">
                <AlertCircle className="w-12 h-12 mx-auto mb-4" />
                <p>{error}</p>
                <button onClick={() => navigate('/apprentice-journal')} className="mt-4 text-plantlix-600 underline">Zurück</button>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-[#1a1a1a] py-8 px-4">
            <div className="max-w-5xl mx-auto">
                {/* Header */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                    <div className="flex items-center gap-3">
                        <button onClick={() => navigate('/apprentice-journal')} className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors">
                            <ArrowLeft className="w-6 h-6 text-gray-600 dark:text-gray-300" />
                        </button>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                                KW {week?.week_number} / {week?.year}
                            </h1>
                            <p className="text-gray-500 text-sm">
                                {week?.start_date && new Date(week.start_date).toLocaleDateString('de-DE')} - {week?.end_date && new Date(week.end_date).toLocaleDateString('de-DE')}
                            </p>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <button onClick={handleRefreshWeather} disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 hover:bg-blue-200 rounded-lg font-medium transition-colors">
                            <RefreshCw className={`w-4 h-4 ${saving ? 'animate-spin' : ''}`} />
                            Wetter aktualisieren
                        </button>
                        <button onClick={handleDownloadPdf} className="flex items-center gap-2 px-4 py-2 bg-gray-800 text-white hover:bg-gray-700 rounded-lg font-medium transition-colors">
                            <Download className="w-4 h-4" />
                            PDF generieren
                        </button>
                        <button onClick={handleDelete} disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-red-100 text-red-700 hover:bg-red-200 rounded-lg font-medium transition-colors" title="Woche Löschen">
                            <Trash2 className="w-4 h-4" />
                        </button>
                        <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-6 py-2 bg-plantlix-500 text-white hover:bg-plantlix-700 rounded-lg font-medium transition-colors">
                            <Save className="w-4 h-4" />
                            {saving ? 'Speichert...' : 'Speichern'}
                        </button>
                    </div>
                </div>

                {error && <div className="p-4 mb-6 bg-red-100 text-red-700 rounded-lg flex items-center gap-2 uppercase text-xs font-bold tracking-wider"><AlertCircle className="w-4 h-4" />{error}</div>}
                {success && <div className="p-4 mb-6 bg-plantlix-100 text-plantlix-700 rounded-lg text-sm font-medium">{success}</div>}

                {/* Settings Card */}
                <div className="bg-white dark:bg-[#1a1a1a] rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-8">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Einstellungen & Wetterübersicht</h3>
                    <div className="grid md:grid-cols-2 gap-6 mb-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Ausbildungsort / Baustelle (Wetterabfrage)</label>
                            <input
                                type="text"
                                className="w-full p-2 border rounded-lg bg-gray-50 dark:bg-gray-800 dark:border-gray-600 focus:ring-2 focus:ring-plantlix-500 dark:text-white"
                                placeholder="z.B. Berlin, Musterstraße 1"
                                value={week?.location_city || ''}
                                onChange={(e) => setWeek({ ...week, location_city: e.target.value })}
                                onBlur={() => handleAutoSaveWeek()}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Schriftart (PDF Berichtsheft)</label>
                            <select 
                                className="w-full p-2 border rounded-lg bg-gray-50 dark:bg-gray-800 dark:border-gray-600 focus:ring-2 focus:ring-plantlix-500 dark:text-white"
                                value={week?.font_style || 'HELVETICA'}
                                onChange={(e) => {
                                    const next = { ...week, font_style: e.target.value };
                                    setWeek(next);
                                    handleAutoSaveWeek(next);
                                }}
                            >
                                <option value="HELVETICA">Helvetica (Klassisch)</option>
                                <option value="TIMES">Times Roman (Elegant)</option>
                                <option value="COURIER">Courier (Schreibmaschine)</option>
                            </select>
                        </div>
                    </div>

                    {/* Weekly Weather Summary Table */}
                    <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 mt-4">
                        <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
                            <thead className="bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
                                <tr>
                                    <th className="px-4 py-3 font-semibold w-32 border-b dark:border-gray-700">Wetter</th>
                                    <th className="px-4 py-3 font-semibold text-center border-b border-l dark:border-gray-700">Niederschlag<br /><span className="text-xs font-normal">in l/m²</span></th>
                                    <th className="px-4 py-3 font-semibold text-center border-b border-l dark:border-gray-700">Höchst-<br /><span className="text-xs font-normal">temperatur</span></th>
                                    <th className="px-4 py-3 font-semibold text-center border-b border-l dark:border-gray-700">Tiefst-<br /><span className="text-xs font-normal">temperatur</span></th>
                                    <th className="px-4 py-3 font-semibold border-b border-l dark:border-gray-700">Wetterbeschreibung</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reports.map((report, idx) => (
                                    <tr key={report.weekday} className="bg-white dark:bg-[#1a1a1a] border-b dark:border-gray-700 last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                                        <td className="px-4 py-2 font-medium text-gray-900 dark:text-white">{WEEKDAYS[idx].label}</td>
                                        <td className="px-4 py-2 text-center border-l dark:border-gray-700">{report.precipitation ?? '-'}</td>
                                        <td className="px-4 py-2 text-center border-l dark:border-gray-700">{report.temp_max ?? '-'}</td>
                                        <td className="px-4 py-2 text-center border-l dark:border-gray-700">{report.temp_min ?? '-'}</td>
                                        <td className="px-4 py-2 border-l dark:border-gray-700">{report.weather || '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Actions & Toggles */}
                <div className="flex flex-wrap gap-3 mb-6 bg-white dark:bg-[#1a1a1a] p-4 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                    <button
                        onClick={expandAll}
                        className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-all font-medium text-sm"
                    >
                        <Maximize2 className="w-4 h-4" /> Alle ausklappen
                    </button>
                    <button
                        onClick={collapseAll}
                        className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-all font-medium text-sm"
                    >
                        <Minimize2 className="w-4 h-4" /> Alle einklappen
                    </button>
                    <div className="flex-1"></div>
                    <button
                        onClick={handleDownloadPdf}
                        disabled={saving}
                        className="flex items-center gap-2 px-6 py-2 bg-plantlix-500 text-white rounded-lg hover:bg-[#1e7a1e] transition-all shadow-md shadow-plantlix-500/20 font-bold"
                    >
                        {saving ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />}
                        Bericht herunterladen
                    </button>
                </div>

                {/* Daily Reports */}
                <div className="space-y-4">
                    {reports.map((report, idx) => (
                        <div key={report.weekday} className="bg-white dark:bg-[#1a1a1a] rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden transition-all duration-300">
                            {/* Day Header - Clickable */}
                            <div 
                                onClick={() => toggleDay(idx)}
                                className={`px-6 py-4 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors ${expandedDays.has(idx) ? 'bg-gray-50/50 dark:bg-gray-800/30 border-b border-gray-100 dark:border-gray-700' : ''}`}
                            >
                                <div className="flex items-center gap-4">
                                    <div className={`p-2 rounded-lg transition-colors ${expandedDays.has(idx) ? 'bg-plantlix-500 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-500'}`}>
                                        {expandedDays.has(idx) ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                                    </div>
                                    <div>
                                        <h4 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                                            {WEEKDAYS[idx].label}
                                            {!expandedDays.has(idx) && report.work_hours && (
                                                <span className="text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 px-2 py-0.5 rounded-full">
                                                    {report.work_hours}h
                                                </span>
                                            )}
                                        </h4>
                                        <span className="text-sm text-gray-500">{new Date(report.date).toLocaleDateString('de-DE')}</span>
                                    </div>
                                </div>

                                {/* Preview when collapsed */}
                                {!expandedDays.has(idx) && (
                                    <div className="flex-1 flex items-center gap-4 max-w-full overflow-hidden">
                                        {report.activities ? (
                                            <p className="text-sm text-gray-500 dark:text-gray-400 truncate italic">
                                                {report.activities.substring(0, 80)}{report.activities.length > 80 ? '...' : ''}
                                            </p>
                                        ) : (
                                            <p className="text-sm text-gray-400 italic">Noch keine Tätigkeiten eingetragen</p>
                                        )}
                                        {report.image && (
                                            <div className="w-8 h-8 rounded border border-gray-200 dark:border-gray-700 overflow-hidden flex-shrink-0">
                                                <img 
                                                    src={report.image instanceof Blob ? URL.createObjectURL(report.image) : (report.image.startsWith('http') ? report.image : `${API_URL}${report.image}`)} 
                                                    alt="Preview" 
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                                    {expandedDays.has(idx) ? (
                                        <>
                                            <div className="flex items-center gap-1"><Sun className="w-4 h-4" /> Max: {report.temp_max ?? '- '}°C</div>
                                            <div className="flex items-center gap-1"><CloudRain className="w-4 h-4" /> {report.precipitation ?? '- '} l/m²</div>
                                        </>
                                    ) : (
                                        <div className="text-xs font-semibold text-plantlix-500 uppercase tracking-wider">
                                            Details anzeigen
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Form Area - Only visible when expanded */}
                            {expandedDays.has(idx) && (
                                <div className="p-6 space-y-8 animate-in slide-in-from-top-2 duration-300">
                                    
                                    {/* Report Type Selection */}
                                    <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-2xl border border-gray-100 dark:border-gray-700">
                                        <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-4 uppercase tracking-wider text-center">Art des Ausbildungstages</label>
                                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                                            {[
                                                { id: 'BETRIEB', label: 'Betrieb', desc: 'Arbeit im Unternehmen', color: 'bg-plantlix-500' },
                                                { id: 'SCHULE', label: 'Schule', desc: 'Berufsschulunterricht', color: 'bg-blue-500' },
                                                { id: 'PRAKTIKUM', label: 'Praktikum', desc: 'Außeneinsatz / Praktikum', color: 'bg-purple-500' },
                                                { id: 'UEBA', label: 'ÜBA', desc: 'Überbetriebl. Ausbildung', color: 'bg-orange-500' }
                                            ].map((type) => (
                                                <button
                                                    key={type.id}
                                                    onClick={() => {
                                                        const updated = [...reports];
                                                        updated[idx].report_type = type.id;
                                                        setReports(updated);
                                                        handleAutoSave(idx);
                                                    }}
                                                    className={`p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-1 ${
                                                        report.report_type === type.id 
                                                        ? `${type.color} text-white border-transparent shadow-lg transform scale-[1.02]` 
                                                        : 'bg-white dark:bg-gray-800 text-gray-500 border-gray-100 dark:border-gray-700 hover:border-gray-200'
                                                    }`}
                                                >
                                                    <span className="text-sm font-bold">{type.label}</span>
                                                    <span className={`text-[10px] opacity-80 ${report.report_type === type.id ? 'text-white' : 'text-gray-400'}`}>{type.desc}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                                        <div className="lg:col-span-2 space-y-6">
                                            {/* Contextual Input Fields */}
                                            {report.report_type === 'SCHULE' ? (
                                                <div className="space-y-2">
                                                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Berufsschulthemen</label>
                                                    <textarea
                                                        className="w-full p-4 border-2 rounded-2xl bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all dark:text-white"
                                                        rows="8"
                                                        placeholder="Welche Fachthemen wurden heute in der Schule behandelt?"
                                                        value={report.school_topics || ''}
                                                        onChange={(e) => handleReportChange(idx, 'school_topics', e.target.value)}
                                                        onBlur={() => handleAutoSave(idx)}
                                                    />
                                                </div>
                                            ) : report.report_type === 'UEBA' ? (
                                                <div className="space-y-2">
                                                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Überbetriebliche Ausbildung (ÜBA)</label>
                                                    <textarea
                                                        className="w-full p-4 border-2 rounded-2xl bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700 focus:ring-4 focus:ring-orange-500/10 focus:border-orange-500 outline-none transition-all dark:text-white"
                                                        rows="8"
                                                        placeholder="Inhalte und Kursnamen der ÜBA..."
                                                        value={report.training_center || ''}
                                                        onChange={(e) => handleReportChange(idx, 'training_center', e.target.value)}
                                                        onBlur={() => handleAutoSave(idx)}
                                                    />
                                                </div>
                                            ) : (
                                                <div className="space-y-2">
                                                    <div className="flex justify-between items-center mb-1">
                                                        <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                                                            {report.report_type === 'PRAKTIKUM' ? 'Praktikumsinhalte' : 'Persönlich ausgeführte Tätigkeiten'}
                                                        </label>
                                                        {report.image && (
                                                            <button 
                                                                onClick={() => handleAiSuggest(idx)}
                                                                disabled={suggestingIndex === idx}
                                                                className="flex items-center gap-1.5 px-3 py-1 bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 rounded-full text-xs font-bold hover:bg-purple-200 transition-colors"
                                                                title="KI-Text aus Foto generieren"
                                                            >
                                                                {suggestingIndex === idx ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Sun className="w-3 h-3" />}
                                                                KI Magic
                                                            </button>
                                                        )}
                                                    </div>
                                                    <textarea
                                                        className="w-full p-4 border-2 rounded-2xl bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700 focus:ring-4 focus:ring-plantlix-500/10 focus:border-plantlix-500 outline-none transition-all dark:text-white"
                                                        rows="8"
                                                        placeholder={report.report_type === 'PRAKTIKUM' ? "Tätigkeiten während des Praktikums..." : "Welche Arbeiten hast du heute selbst ausgeführt?"}
                                                        value={report.activities || ''}
                                                        onChange={(e) => handleReportChange(idx, 'activities', e.target.value)}
                                                        onBlur={() => handleAutoSave(idx)}
                                                    />
                                                </div>
                                            )}

                                            <div className="space-y-2">
                                                <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Besondere Vorkommnisse / Fehlzeiten</label>
                                                <input
                                                    className="w-full p-4 border-2 rounded-2xl bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700 focus:ring-4 focus:ring-red-500/10 focus:border-red-400 outline-none transition-all dark:text-white"
                                                    type="text"
                                                    placeholder="Urlaub, Krankheit, Berufsschule (ganztätig), Regenpause..."
                                                    value={report.absences || ''}
                                                    onChange={(e) => handleReportChange(idx, 'absences', e.target.value)}
                                                    onBlur={() => handleAutoSave(idx)}
                                                />
                                            </div>
                                        </div>

                                        <div className="space-y-8">
                                            {/* Location - For all types */}
                                            <div className="space-y-2">
                                                <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Baustelle / Standort</label>
                                                <div className="relative">
                                                    <input
                                                        className="w-full p-4 pl-12 border-2 rounded-2xl bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700 focus:ring-4 focus:ring-plantlix-500/10 focus:border-plantlix-500 outline-none transition-all dark:text-white"
                                                        type="text"
                                                        placeholder="Wo warst du heute?"
                                                        value={report.location || ''}
                                                        onChange={(e) => handleReportChange(idx, 'location', e.target.value)}
                                                        onBlur={() => handleAutoSave(idx)}
                                                    />
                                                    <Sun className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                                </div>
                                            </div>

                                            {/* Weather Details - For all types */}
                                            <div className="bg-blue-50/50 dark:bg-blue-900/10 p-5 rounded-3xl border border-blue-100 dark:border-blue-900/40 space-y-4">
                                                <h5 className="text-xs font-bold text-blue-600 dark:text-blue-400 uppercase tracking-widest flex items-center gap-2">
                                                    <CloudRain className="w-4 h-4" /> Wetter am Tag
                                                </h5>
                                                <div className="grid grid-cols-2 gap-3">
                                                    <div className="space-y-1">
                                                        <label className="text-[10px] font-bold text-blue-800 dark:text-blue-300 uppercase">Max (°C)</label>
                                                        <input
                                                            type="number"
                                                            className="w-full p-2.5 text-sm bg-white dark:bg-gray-800 border border-blue-200 dark:border-blue-800 rounded-xl dark:text-white"
                                                            value={report.temp_max ?? ''}
                                                            onChange={(e) => handleReportChange(idx, 'temp_max', e.target.value === '' ? null : e.target.value)}
                                                            onBlur={() => handleAutoSave(idx)}
                                                        />
                                                    </div>
                                                    <div className="space-y-1">
                                                        <label className="text-[10px] font-bold text-blue-800 dark:text-blue-300 uppercase">Min (°C)</label>
                                                        <input
                                                            type="number"
                                                            className="w-full p-2.5 text-sm bg-white dark:bg-gray-800 border border-blue-200 dark:border-blue-800 rounded-xl dark:text-white"
                                                            value={report.temp_min ?? ''}
                                                            onChange={(e) => handleReportChange(idx, 'temp_min', e.target.value === '' ? null : e.target.value)}
                                                            onBlur={() => handleAutoSave(idx)}
                                                        />
                                                    </div>
                                                    <div className="col-span-2 space-y-1">
                                                        <label className="text-[10px] font-bold text-blue-800 dark:text-blue-300 uppercase">Niederschlag (l/m²)</label>
                                                        <input
                                                            type="number"
                                                            className="w-full p-2.5 text-sm bg-white dark:bg-gray-800 border border-blue-200 dark:border-blue-800 rounded-xl dark:text-white"
                                                            value={report.precipitation ?? ''}
                                                            onChange={(e) => handleReportChange(idx, 'precipitation', e.target.value === '' ? null : e.target.value)}
                                                            onBlur={() => handleAutoSave(idx)}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Working Hours */}
                                            <div className="bg-gray-50 dark:bg-gray-800 p-5 rounded-3xl border border-gray-100 dark:border-gray-700">
                                                <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-3">Arbeitszeit</label>
                                                <div className="flex items-center gap-4">
                                                    <input
                                                        type="number"
                                                        step="0.5"
                                                        className="w-24 p-3 border-2 rounded-xl bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700 focus:border-plantlix-500 outline-none transition-all dark:text-white font-bold"
                                                        value={report.work_hours ?? ''}
                                                        onChange={(e) => handleReportChange(idx, 'work_hours', e.target.value === '' ? null : e.target.value)}
                                                        onBlur={() => handleAutoSave(idx)}
                                                    />
                                                    <span className="text-sm font-bold text-gray-500">Stunden</span>
                                                </div>
                                            </div>
                                            
                                            {/* Location Map Section */}
                                            <div className="bg-gray-50 dark:bg-gray-800 p-5 rounded-3xl border border-gray-100 dark:border-gray-700 space-y-4">
                                                <div className="flex items-center justify-between mb-2">
                                                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest">Einsatzort Karte</label>
                                                    <button 
                                                        onClick={() => {
                                                            const updated = [...reports];
                                                            updated[idx].show_map = !updated[idx].show_map;
                                                            setReports(updated);
                                                            handleAutoSave(idx);
                                                        }}
                                                        className={`flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-black transition-all ${report.show_map ? 'bg-plantlix-500 text-white' : 'bg-gray-600 text-white'}`}
                                                    >
                                                        {report.show_map ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                                                        {report.show_map ? 'IM PDF' : 'NICHT IM PDF'}
                                                    </button>
                                                </div>
                                                <div className="rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700">
                                                    <LocationMap 
                                                        lat={report.latitude}
                                                        lng={report.longitude}
                                                        isEditable={true}
                                                        onLocationSelect={(lat, lng) => {
                                                            handleReportChange(idx, 'latitude', lat);
                                                            handleReportChange(idx, 'longitude', lng);
                                                            handleAutoSave(idx);
                                                        }}
                                                        height="150px"
                                                        zoom={14}
                                                    />
                                                </div>
                                                <p className="text-[10px] text-center text-gray-400 font-medium">
                                                    Klicke auf die Karte, um den genauen Standort zu markieren
                                                </p>
                                            </div>

                                            {/* Daily Image Section */}
                                            <div className="pt-2">
                                                <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-3">Foto des Tages</label>
                                                {report.image ? (
                                                    <div className="space-y-3">
                                                        <div className="relative group rounded-3xl overflow-hidden border-4 border-white dark:border-gray-800 shadow-xl">
                                                            <img 
                                                                src={report.image instanceof Blob ? URL.createObjectURL(report.image) : (report.image.startsWith('http') ? report.image : `${API_URL}${report.image}`)} 
                                                                alt="Daily preview" 
                                                                className="w-full h-48 object-cover transition-transform group-hover:scale-105"
                                                            />
                                                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                                                <button 
                                                                    onClick={() => removeImage(idx)}
                                                                    className="p-3 bg-red-500 text-white rounded-2xl hover:bg-red-600 transition-all font-bold flex items-center gap-2"
                                                                >
                                                                    <Trash2 className="w-5 h-5" /> Löschen
                                                                </button>
                                                                <button 
                                                                    onClick={() => {
                                                                        const updated = [...reports];
                                                                        updated[idx].show_image = !updated[idx].show_image;
                                                                        setReports(updated);
                                                                        handleAutoSave(idx);
                                                                    }}
                                                                    className={`p-3 rounded-2xl transition-all font-bold flex items-center gap-2 ${report.show_image ? 'bg-white text-gray-800' : 'bg-gray-600 text-white'}`}
                                                                >
                                                                    {report.show_image ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                                                                    {report.show_image ? 'PDF Ein' : 'PDF Aus'}
                                                                </button>
                                                            </div>
                                                        </div>
                                                        <label className="flex items-center justify-center gap-2 w-full p-3 bg-gray-100 dark:bg-gray-800 rounded-xl cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700 transition-all text-sm font-bold text-gray-600 dark:text-gray-300">
                                                            <ImageIcon className="w-5 h-5" /> Foto ersetzen
                                                            <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageSelect(idx, e)} />
                                                        </label>
                                                    </div>
                                                ) : (
                                                    <label className="flex flex-col items-center justify-center gap-3 w-full h-48 bg-gray-50 dark:bg-gray-800/50 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-3xl cursor-pointer hover:border-plantlix-500 dark:hover:border-plantlix-500 group transition-all">
                                                        <div className="p-4 bg-white dark:bg-gray-800 rounded-2xl shadow-sm text-gray-400 group-hover:text-plantlix-500 group-hover:scale-110 transition-all">
                                                            <Camera className="w-8 h-8" />
                                                        </div>
                                                        <span className="text-sm font-bold text-gray-400 group-hover:text-gray-600">Foto hinzufügen</span>
                                                        <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageSelect(idx, e)} />
                                                    </label>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* Cropper Modal */}
                {croppingIndex !== null && (
                    <ImageCropper
                        image={selectedImage}
                        onCropComplete={handleCropComplete}
                        onCancel={() => {
                            setCroppingIndex(null);
                            setSelectedImage(null);
                        }}
                    />
                )}
                {/* Custom Confirmation Modal */}
                {confirmModal.show && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
                        <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-gray-200 dark:border-gray-700 animate-in zoom-in-95 duration-200">
                            <div className="p-6">
                                <div className="flex items-center gap-3 mb-4 text-red-500">
                                    <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded-full">
                                        <AlertTriangle className="w-6 h-6" />
                                    </div>
                                    <h3 className="text-xl font-bold dark:text-white">{confirmModal.title}</h3>
                                </div>
                                <p className="text-gray-600 dark:text-gray-400 mb-6 font-medium">
                                    {confirmModal.message}
                                </p>
                                <div className="flex justify-end gap-3">
                                    <button
                                        onClick={() => setConfirmModal({ ...confirmModal, show: false })}
                                        className="px-5 py-2.5 rounded-xl font-semibold border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all"
                                    >
                                        Abbrechen
                                    </button>
                                    <button
                                        onClick={() => {
                                            confirmModal.onConfirm();
                                            setConfirmModal({ ...confirmModal, show: false });
                                        }}
                                        className="px-5 py-2.5 rounded-xl font-semibold bg-red-500 hover:bg-red-600 text-white shadow-lg shadow-red-500/20 transition-all"
                                    >
                                        Löschen bestätigen
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
